import type { DateRange } from 'react-day-picker';
import { parseDateOnlyLocal } from '@erp/shared-utils';
import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
} from 'date-fns';

/** Inclusive calendar-day bounds as ISO `YYYY-MM-DD`; either side optional. */
export interface DateOnlyRange {
  from?: string;
  to?: string;
}

export type PresetKey =
  'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'all';

const WEEK_OPTS = { weekStartsOn: 1 } as const;

export function toDateOnly(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Date-only bounds → the calendar's `DateRange` (undefined when both blank). */
export function toDayPickerRange(value: DateOnlyRange): DateRange | undefined {
  const from = value.from ? parseDateOnlyLocal(value.from) : null;
  const to = value.to ? parseDateOnlyLocal(value.to) : null;
  if (!from && !to) return undefined;
  return { from: from ?? undefined, to: to ?? undefined };
}

/** The calendar's `DateRange` → date-only bounds. */
export function fromDayPickerRange(
  range: DateRange | undefined,
): DateOnlyRange {
  return {
    from: range?.from ? toDateOnly(range.from) : undefined,
    to: range?.to ? toDateOnly(range.to) : undefined,
  };
}

/** Each preset's range, computed against the local calendar today. */
export function resolvePreset(
  preset: PresetKey,
  today: Date = new Date(),
): DateOnlyRange {
  switch (preset) {
    case 'thisWeek':
      return {
        from: toDateOnly(startOfWeek(today, WEEK_OPTS)),
        to: toDateOnly(endOfWeek(today, WEEK_OPTS)),
      };
    case 'thisMonth':
      return {
        from: toDateOnly(startOfMonth(today)),
        to: toDateOnly(endOfMonth(today)),
      };
    case 'lastMonth': {
      const lastMonth = subMonths(today, 1);
      return {
        from: toDateOnly(startOfMonth(lastMonth)),
        to: toDateOnly(endOfMonth(lastMonth)),
      };
    }
    case 'thisYear':
      return {
        from: toDateOnly(startOfYear(today)),
        to: toDateOnly(endOfYear(today)),
      };
    case 'all':
      return {};
  }
}
